import React, { useState, useEffect } from 'react';
import { collection, query, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';

const BlogManager = ({ db, auth, user, appId }) => {
    const [blogPosts, setBlogPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingPost, setEditingPost] = useState(null);
    const [editedImageUrl, setEditedImageUrl] = useState('');

    useEffect(() => {
        if (!db || !user) return;

        // Try both collection paths
        const collectionPath = appId ? `/artifacts/${appId}/public/data/blogPosts` : 'blogPosts';
        console.log('BlogManager: Using collection path:', collectionPath);
        
        const q = query(collection(db, collectionPath));
        
        const unsubscribe = onSnapshot(q, 
            (querySnapshot) => {
                const posts = [];
                querySnapshot.forEach((doc) => {
                    posts.push({ id: doc.id, ...doc.data() });
                });
                setBlogPosts(posts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
                setLoading(false);
            },
            (error) => {
                console.error('Error fetching blog posts:', error);
                // Try fallback path
                if (appId) {
                    const fallbackQ = query(collection(db, 'blogPosts'));
                    const fallbackUnsubscribe = onSnapshot(fallbackQ,
                        (querySnapshot) => {
                            const posts = [];
                            querySnapshot.forEach((doc) => {
                                posts.push({ id: doc.id, ...doc.data() });
                            });
                            setBlogPosts(posts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
                            setLoading(false);
                        },
                        (fallbackError) => {
                            console.error('Error with fallback path:', fallbackError);
                            setLoading(false);
                        }
                    );
                    return fallbackUnsubscribe;
                }
                setLoading(false);
            }
        );

        return unsubscribe;
    }, [db, user, appId]);

    const handleUpdateImage = async (postId) => {
        if (!editedImageUrl.trim()) {
            alert('Zadejte URL obrázku');
            return;
        }

        try {
            const collectionPath = appId ? `/artifacts/${appId}/public/data/blogPosts` : 'blogPosts';
            const postRef = doc(db, collectionPath, postId);
            
            await updateDoc(postRef, {
                image: editedImageUrl
            });

            setEditingPost(null);
            setEditedImageUrl('');
            alert('Obrázek byl úspěšně aktualizován!');
        } catch (error) {
            console.error('Error updating image:', error);
            // Try fallback path
            if (appId) {
                try {
                    const fallbackPostRef = doc(db, 'blogPosts', postId);
                    await updateDoc(fallbackPostRef, {
                        image: editedImageUrl
                    });
                    setEditingPost(null);
                    setEditedImageUrl('');
                    alert('Obrázek byl úspěšně aktualizován!');
                } catch (fallbackError) {
                    console.error('Error with fallback update:', fallbackError);
                    alert('Chyba při aktualizaci obrázku: ' + fallbackError.message);
                }
            } else {
                alert('Chyba při aktualizaci obrázku: ' + error.message);
            }
        }
    };

    const handleDeletePost = async (postId, postTitle) => {
        if (!window.confirm(`Opravdu chcete smazat příspěvek "${postTitle}"?`)) {
            return;
        }

        try {
            const collectionPath = appId ? `/artifacts/${appId}/public/data/blogPosts` : 'blogPosts';
            await deleteDoc(doc(db, collectionPath, postId));
            alert('Příspěvek byl smazán!');
        } catch (error) {
            console.error('Error deleting post:', error);
            // Try fallback path
            if (appId) {
                try {
                    await deleteDoc(doc(db, 'blogPosts', postId));
                    alert('Příspěvek byl smazán!');
                } catch (fallbackError) {
                    console.error('Error with fallback delete:', fallbackError);
                    alert('Chyba při mazání příspěvku: ' + fallbackError.message);
                }
            } else {
                alert('Chyba při mazání příspěvku: ' + error.message);
            }
        }
    };

    const startEditing = (post) => {
        setEditingPost(post.id);
        setEditedImageUrl(post.image || '');
    };

    const cancelEditing = () => {
        setEditingPost(null);
        setEditedImageUrl('');
    };

    if (loading) {
        return (
            <div className="text-center py-8">
                <p className="text-gray-400">Načítám blog příspěvky...</p>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto">
            <div className="mb-8">
                <h2 className="text-3xl font-bold mb-4">Správa existujících blog příspěvků</h2>
                <p className="text-gray-400">Zde můžete upravit URL obrázků u existujících blog příspěvků.</p>
            </div>

            {blogPosts.length === 0 ? (
                <div className="text-center py-8">
                    <p className="text-gray-400">Žádné blog příspěvky nebyly nalezeny.</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {blogPosts.map((post) => (
                        <div key={post.id} className="bg-gray-800 rounded-xl p-6">
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <h3 className="text-xl font-bold mb-2">{post.title}</h3>
                                    <p className="text-gray-400 text-sm mb-2">
                                        Datum: {post.date} | Autor: {post.author}
                                    </p>
                                    <p className="text-gray-400 text-sm mb-2 flex items-center">
                                        <i className="fas fa-eye mr-2 text-yellow-500"></i>
                                        Zobrazení: <span className="font-bold ml-1 text-yellow-400">{post.viewCount || 0}</span>
                                    </p>
                                    <p className="text-gray-300 mb-4">{post.summary}</p>
                                    
                                    {editingPost === post.id ? (
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-white text-sm font-medium mb-2">
                                                    URL obrázku:
                                                </label>
                                                <input
                                                    type="url"
                                                    value={editedImageUrl}
                                                    onChange={(e) => setEditedImageUrl(e.target.value)}
                                                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                                                    placeholder="https://firebasestorage.googleapis.com/..."
                                                />
                                            </div>
                                            <div className="flex space-x-3">
                                                <button
                                                    onClick={() => handleUpdateImage(post.id)}
                                                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
                                                >
                                                    Uložit
                                                </button>
                                                <button
                                                    onClick={cancelEditing}
                                                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
                                                >
                                                    Zrušit
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <p className="text-gray-400 text-sm mb-3">
                                                Aktuální obrázek: {post.image ? (
                                                    <span className="text-green-400">✓ Nastaven</span>
                                                ) : (
                                                    <span className="text-red-400">✗ Chybí</span>
                                                )}
                                            </p>
                                            {post.image && (
                                                <div className="mb-3">
                                                    <code className="bg-gray-700 p-2 rounded text-xs text-green-400 break-all">
                                                        {post.image}
                                                    </code>
                                                </div>
                                            )}
                                            <div className="flex space-x-3">
                                                <button
                                                    onClick={() => startEditing(post)}
                                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm"
                                                >
                                                    {post.image ? 'Změnit obrázek' : 'Přidat obrázek'}
                                                </button>
                                                <button
                                                    onClick={() => handleDeletePost(post.id, post.title)}
                                                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm"
                                                >
                                                    Smazat příspěvek
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="ml-6">
                                    {post.image ? (
                                        <img 
                                            src={post.image} 
                                            alt={post.title}
                                            className="w-32 h-32 object-cover rounded-lg"
                                            onError={(e) => {
                                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4IiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDEyOCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjgiIGhlaWdodD0iMTI4IiBmaWxsPSIjMzc0MTUxIi8+CjxwYXRoIGQ9Ik02NCA0OEM2Ny4zMTM3IDQ4IDcwIDUwLjY4NjMgNzAgNTRDNzAgNTcuMzEzNyA2Ny4zMTM3IDYwIDY0IDYwQzYwLjY4NjMgNjAgNTggNTcuMzEzNyA1OCA1NEM1OCA1MC42ODYzIDYwLjY4NjMgNDggNjQgNDhaIiBmaWxsPSIjNkI3Mjg1Ii8+CjxwYXRoIGQ9Ik00MCA4OEg4OFY5Nkg0MFY4OFoiIGZpbGw9IiM2QjcyODUiLz4KPC9zdmc+';
                                            }}
                                        />
                                    ) : (
                                        <div className="w-32 h-32 bg-gray-700 rounded-lg flex items-center justify-center">
                                            <i className="fas fa-image text-gray-500 text-2xl"></i>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default BlogManager;